import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorMessage } from '@ds';
import CaseTabs from './CaseTabs/CaseTabs';
import HomeSkeleton from './HomeSkeleton/HomeSkeleton';
import { CaseDetails } from 'src/components/CaseDetails/CaseDetails';
import { useGetActiveCases } from 'src/shared/queries/useGetActiveCases';
import { getUserIcon } from 'src/utils/services/User';
import { useGetCaseDetails } from 'src/shared/queries/useGetCaseDetails';
import { useGetUserContacts } from 'src/shared/queries/useGetUserContacts';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { AddressType } from 'src/shared/types/Common';
import useSmartScroll from 'src/utils/hooks/useSmartScroll';
import ImmigrationSnapshot from './ImmigrationSnapshot/ImmigrationSnapshot';
import CaseTimeline from 'src/components/CaseTimeline/CaseTimeline';
import { ICaseInfo } from 'src/shared/types/Case';
import PendingConfirmationBanner from 'src/components/PendingConfirmationBanner/PendingConfirmationBanner';
import { AddressConfirmationType } from 'src/utils/constants/automation';
import styles from './Home.module.scss';
import { useImmigrationInstanceStore } from 'src/stores/ImmigrationInstanceStore';
import { useGetAutomationPendingConfirmation } from 'src/shared/queries/useGetAutomationPendingConfirmation';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';
import I94Banner from 'src/components/I94Banner/I94Banner';

const Home: React.FC = () => {
  const { data: userInfo } = useGetUser();
  const userId = Number(userInfo?.userId ? Number(userInfo.userId) : undefined);
  const { getParam, setParam } = useNavigateWithSecureParams();
  const caseIdAsNumber = Number(getParam('caseId'));
  const [selectedCaseId, setSelectedCaseId] = useState<number>(caseIdAsNumber || 0);
  const [selectedCaseDetails, setSelectedCaseDetails] = useState<ICaseInfo | null>(null);
  const { data: activeCases, isLoading: isActiveCasesLoading, error: activeCasesError } = useGetActiveCases();
  const {
    data: caseDetails,
    isLoading: isLoadingCaseDetails,
    error: caseDetailsError,
  } = useGetCaseDetails(selectedCaseId);
  const casesContainerRef = useRef<HTMLDivElement | null>(null);
  const snapshotContainerRef = useRef<HTMLDivElement | null>(null);
  const { immigrationInstance } = useImmigrationInstanceStore();

  // Get pending confirmations for banner
  const { data: pendingConfirmations } = useGetAutomationPendingConfirmation(userInfo?.beneId || 0);

  const hasPendingAddressConfirmation =
    pendingConfirmations?.some(
      (confirmation: any) => confirmation.type === AddressConfirmationType.BeneficiaryLandingPage
    ) ?? false;
  useSmartScroll(casesContainerRef);
  useSmartScroll(snapshotContainerRef);

  const shouldFetchUserContacts =
    !isLoadingCaseDetails && caseDetails && (!caseDetails.workLocations || caseDetails.workLocations.length === 0);

  const { data: userContacts } = useGetUserContacts(shouldFetchUserContacts ? userId : undefined);

  const resolvedWorkLocations = useMemo(() => {
    if (!caseDetails) return [];

    if ((caseDetails?.workLocations?.length ?? 0) > 0) {
      return caseDetails.workLocations;
    }

    return userContacts?.contactAddresses?.filter((contact) => contact.addressType === AddressType.WORK) || [];
  }, [caseDetails?.workLocations, userContacts, selectedCaseId]);

  useEffect(() => {
    if (activeCases && activeCases.length > 0 && caseDetails) {
      const activeCase = activeCases?.find(({ caseId }) => caseId === selectedCaseId);
      setSelectedCaseDetails({
        ...caseDetails,
        workLocations: resolvedWorkLocations,
        dependents: activeCase?.dependents || [],
        isDependentOnly: activeCase?.isDependentOnly,
        universalCaseId: activeCase?.universalCaseId,
        remoteCaseId: activeCase?.remoteCaseId,
        isRemote:
          immigrationInstance !== undefined &&
          activeCase?.sourceInstance !== undefined &&
          immigrationInstance !== activeCase.sourceInstance,
      });
    }
  }, [activeCases, caseDetails, selectedCaseId, resolvedWorkLocations, immigrationInstance]);

  useEffect(() => {
    if (!activeCases) {
      return;
    }

    // Check if the case from URL param exists in active cases
    const validCaseFromUrl = caseIdAsNumber && activeCases.some((caseInfo) => caseInfo.caseId === caseIdAsNumber);

    if (validCaseFromUrl && selectedCaseId !== caseIdAsNumber) {
      setSelectedCaseId(caseIdAsNumber);
    } else if (
      activeCases.length > 0 &&
      (!selectedCaseId || !activeCases.some((caseInfo) => caseInfo.caseId === selectedCaseId))
    ) {
      // If no valid selectedCaseId or current selectedCaseId is not in active cases, use first active case
      setSelectedCaseId(activeCases[0].caseId);
    }
  }, [selectedCaseId, activeCases, caseIdAsNumber]);

  const tabs = useMemo(() => {
    return (
      activeCases?.map((caseInfo) => ({
        id: caseInfo.caseId,
        name: caseInfo.caseName,
        userIcon: getUserIcon(caseInfo?.dependents, caseInfo?.isDependentOnly as boolean),
        subTypes: caseInfo.caseSubTypes,
        dependents: caseInfo.dependents,
        isDependentOnly: caseInfo.isDependentOnly,
      })) || []
    );
  }, [activeCases]);

  const onTabSelect = (id: number) => {
    if (id !== selectedCaseId) {
      setSelectedCaseId(id);
      setSelectedCaseDetails(null);
      setParam('caseId', id.toString());
    }
  };

  return (
    <div className={styles['home-page']}>
      <div className={styles['home-page__header']}>
        <h2 className={styles['home-page__title']}>Home</h2>
        {userInfo?.beneId && <I94Banner beneId={userInfo.beneId} />}
      </div>
      {hasPendingAddressConfirmation && <PendingConfirmationBanner />}
      <div className={styles['home-page__content']}>
        <div ref={casesContainerRef} className={styles['home-page__cases']}>
          <h3>CASES</h3>
          {isActiveCasesLoading ? (
            <HomeSkeleton />
          ) : activeCasesError ? (
            <ErrorMessage error={activeCasesError} />
          ) : (
            <>
              {activeCases && <CaseTabs tabs={tabs} selectedTabId={selectedCaseId} onTabSelect={onTabSelect} />}
              {activeCases && activeCases.length > 0 && (isLoadingCaseDetails || !selectedCaseDetails) ? (
                caseDetailsError ? (
                  <ErrorMessage error={caseDetailsError} />
                ) : (
                  <HomeSkeleton showTabs={false} />
                )
              ) : (
                selectedCaseDetails && (
                  <>
                    <CaseDetails caseDetails={selectedCaseDetails} />
                    <CaseTimeline caseDetails={selectedCaseDetails} />
                  </>
                )
              )}
            </>
          )}
        </div>
        <div className={styles['home-page__immigration-status']} ref={snapshotContainerRef}>
          <ImmigrationSnapshot />
        </div>
      </div>
    </div>
  );
};

export default Home;
